import { atom } from "jotai";
import { atomFamily, loadable } from "jotai/utils";
import deepEqual from "fast-deep-equal";

import {
  fetchInterfaceProjectAtomFamily_SUSPENDS,
  fetchChatbotAtomFamily_SUSPENDS,
  fetchAgentAtomFamily_SUSPENDS,
  tableAtomFamily_SUSPENDS,
} from "../apps";
import {
  AgentFetchResult,
  AssetType,
  ChatbotFetchResult,
  InterfaceFetchResult,
} from "../types";
import {
  zapAtomFamily_SUSPENDS,
  zapMetadataAtomFamily_SUSPENDS,
} from "../zaps";
import { isZapErrorObject } from "../utils";

/**
 * Suspended variant that computes asset access status directly from
 * the asset-access fetch atoms.  The atom **suspends** while any
 * underlying asset call is pending.
 *
 * A node is forbidden / locked when *any* of the following is true:
 * • Table     → fetch returns `{ type: "forbidden" }`
 * • Interface → fetch returns `null`
 * • Chatbot   → fetch returns `null`
 * • Agent     → fetch returns `{ type: "forbidden" }`
 * • Zap       → forbidden when zapData returns error (403) but zapMetadata succeeds (means zap is found)
 *
 * Other table and agent results (`null`, `not_found`, `success`) do NOT lock.
 *
 * @returns
 *   `{
 *     isAssetForbidden: boolean,
 *     isAssetNotFound: boolean,
 *     isAssetInaccessible: boolean
 *   }`
 */
export const assetAccessAtomFamily_SUSPENDS = atomFamily(
  (params: {
    assetId?: string | undefined | null;
    interfaceProjectId?: string;
    assetType: AssetType;
  }) => {
    const baseAtom = atom(async (get) => {
      const { assetId, interfaceProjectId, assetType } = params;

      const zapId = assetType === "zap" ? assetId : undefined;
      const tableId = assetType === "table" ? assetId : undefined;
      const interfaceId =
        assetType === "interface" ? interfaceProjectId : undefined;
      const chatbotId = assetType === "chatbot" ? assetId : undefined;
      const agentId = assetType === "agent" ? assetId : undefined;

      /* ---------------- asset look-ups (each may suspend) ---------------- */
      const [zapMetadataResult, zapResult] = zapId
        ? await Promise.allSettled([
            get(zapMetadataAtomFamily_SUSPENDS(zapId)),
            get(zapAtomFamily_SUSPENDS(zapId)),
          ])
        : [null, null];

      const zapMetadata =
        zapMetadataResult?.status === "fulfilled"
          ? zapMetadataResult.value
          : null;

      const zapData =
        zapResult?.status === "fulfilled" ? zapResult.value : null;

      const tableFieldsResult = tableId
        ? await get(tableAtomFamily_SUSPENDS(tableId))
        : undefined;

      const interfaceResult = interfaceId
        ? await get(fetchInterfaceProjectAtomFamily_SUSPENDS(interfaceId))
        : undefined;

      const chatbotResult = chatbotId
        ? await get(fetchChatbotAtomFamily_SUSPENDS(chatbotId))
        : undefined;

      const agentResult = agentId
        ? await get(fetchAgentAtomFamily_SUSPENDS(agentId))
        : undefined;

      /* ---------------- access evaluation ---------------- */
      const isZapNotFound = isZapErrorObject(zapMetadata);
      // This is the only way to tell if a zap is 403 and not 404 since zapData returns 404 for both
      const isZapForbidden = !isZapNotFound && isZapErrorObject(zapData);

      const isChatbotForbidden =
        (chatbotResult as ChatbotFetchResult | null | undefined)?.type ===
        "forbidden";
      const isChatbotNotFound =
        (chatbotResult as ChatbotFetchResult | null | undefined)?.type ===
        "not_found";

      const isTableForbidden = tableFieldsResult?.type === "forbidden";
      const isTableNotFound = tableFieldsResult?.type === "not_found";

      const isInterfaceForbidden =
        (interfaceResult as InterfaceFetchResult | null | undefined)?.type ===
        "forbidden";
      const isInterfaceNotFound =
        (interfaceResult as InterfaceFetchResult | null | undefined)?.type ===
        "not_found";

      const isAgentForbidden =
        (agentResult as AgentFetchResult | null | undefined)?.type ===
        "forbidden";
      const isAgentNotFound =
        (agentResult as AgentFetchResult | null | undefined)?.type ===
        "not_found";

      // 403
      const isAssetForbidden =
        isZapForbidden ||
        isTableForbidden ||
        isInterfaceForbidden ||
        isChatbotForbidden ||
        isAgentForbidden;

      // 404
      const isAssetNotFound =
        isZapNotFound ||
        isTableNotFound ||
        isInterfaceNotFound ||
        isAgentNotFound ||
        isChatbotNotFound;

      return {
        isAssetForbidden, // 403
        isAssetNotFound, // 404
        isAssetInaccessible: isAssetForbidden || isAssetNotFound,
      };
    });

    baseAtom.debugLabel = `assetAccessAtomFamily_SUSPENDS(${JSON.stringify(
      params,
    )})`;
    return baseAtom;
  },
  deepEqual,
);

// Does NOT suspend due to loadable usage
export const loadableAssetAccessAsyncAtomFamily = atomFamily(
  (params: {
    assetId?: string | undefined | null;
    interfaceProjectId?: string;
    assetType: AssetType;
  }) => {
    const loadableAssetAccessAsyncAtom = loadable(
      assetAccessAtomFamily_SUSPENDS(params),
    );
    loadableAssetAccessAsyncAtom.debugLabel = `loadableAssetAccessAsyncAtomFamily(${params.assetType}:${params.assetId}:${params.interfaceProjectId})`;
    return loadableAssetAccessAsyncAtom;
  },
  deepEqual,
);
